<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
        <div>
          <h2 class="text-white font-weight-bold mb-1">{{ $t('superadmin.packagesTitle') }}</h2>
          <p class="text-muted small mb-0">{{ $t('superadmin.packagesSubtitle') }}</p>
        </div>
        <div>
          <router-link
            :to="{ name: 'CreatePackage' }"
            class="btn btn-primary"
          >
            <i class="mdi mdi-plus mr-1"></i>
            {{ $t('superadmin.createPackage') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Package Table Card -->
    <div class="col-12 grid-margin">
      <div class="card">
        <div class="card-body">
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div class="search-box" style="min-width: 320px; max-width: 440px; width: 100%;">
              <div class="input-group">
                <span class="input-group-text">
                  <i class="mdi mdi-magnify"></i>
                </span>
                <input
                  v-model.trim="search"
                  type="text"
                  class="form-control"
                  :placeholder="$t('common.search') + ' plan name, features...'"
                />
                <button v-if="search" class="btn btn-outline-secondary" type="button" @click="search = ''">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
            <div class="text-muted small">
              {{ $t('common.all') }}: <span class="text-white font-weight-medium">{{ filteredItems.length }}</span>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table align-middle" style="width: 100%;">
              <thead>
                <tr>
                  <th style="width: 50px;">No</th>
                  <th style="min-width: 190px;">{{ $t('superadmin.packageName') }}</th>
                  <th style="min-width: 150px;">{{ $t('superadmin.pricePerSeatMonth') }}</th>
                  <th style="min-width: 185px;">{{ $t('superadmin.maxUserSeats') }}</th>
                  <th style="min-width: 280px;">{{ $t('superadmin.packageDescription') }}</th>
                  <th class="text-right" style="width: 95px; padding-right: 1rem;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(pkg, index) in paginatedPackages" :key="pkg.id || index">
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div
                        class="rounded-md mr-2 d-flex align-items-center justify-content-center flex-shrink-0"
                        style="width: 36px; height: 36px; background: rgba(99, 102, 241, 0.12); border: 1px solid rgba(99, 102, 241, 0.25); border-radius: 8px;"
                      >
                        <i class="mdi mdi-cube-outline text-primary" style="font-size: 20px;"></i>
                      </div>
                      <div class="overflow-hidden">
                        <router-link
                          :to="{ name: 'DetailPackage', params: { id: pkg.id } }"
                          class="text-white font-weight-semibold text-decoration-none d-block text-truncate"
                          :title="pkg.package_name"
                        >
                          {{ pkg.package_name }}
                        </router-link>
                        <div class="text-muted small text-truncate" v-if="pkg.trial_days">
                          {{ pkg.trial_days }} {{ $currentLocale === 'id' ? 'Hari Uji Coba Gratis' : 'Days Free Trial' }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="text-nowrap">
                    <span class="text-success font-weight-bold">
                      {{ formatRupiah(pkg.price_per_person_per_month) }}
                    </span>
                    <span class="text-muted small">/{{ $t('quota.seats') }}/{{ $currentLocale === 'id' ? 'bln' : 'mo' }}</span>
                  </td>
                  <td class="text-nowrap">
                    <span class="badge badge-outline-warning font-weight-medium">
                      {{ pkg.limit_user ? `${pkg.limit_user} ${$t('quota.seats')}` : ($currentLocale === 'id' ? 'Tak Terbatas' : 'Unlimited') }}
                    </span>
                  </td>
                  <td>
                    <div class="text-muted small" style="line-height: 1.5; max-width: 380px;" :title="pkg.description">
                      {{ pkg.description || '-' }}
                    </div>
                  </td>
                  <td class="text-right">
                    <div class="d-inline-flex align-items-center gap-1">
                      <router-link
                        :to="{ name: 'DetailPackage', params: { id: pkg.id } }"
                        class="btn btn-action-icon btn-outline-info"
                        :title="$t('common.view')"
                      >
                        <i class="mdi mdi-eye"></i>
                      </router-link>
                      <router-link
                        :to="{ name: 'EditPackage', params: { id: pkg.id } }"
                        class="btn btn-action-icon btn-outline-warning"
                        :title="$t('common.edit')"
                      >
                        <i class="mdi mdi-pencil"></i>
                      </router-link>
                    </div>
                  </td>
                </tr>

                <tr v-if="paginatedPackages.length === 0">
                  <td colspan="6" class="text-center py-5">
                    <EmptyState
                      icon="mdi-package-variant-closed"
                      :title="$t('superadmin.packagesTitle')"
                      :description="$t('superadmin.packagesSubtitle')"
                      :actionText="$t('superadmin.createPackage')"
                      actionTo="/package/add"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="filteredItems.length > itemsPerPage" class="mt-3">
            <PaginationComponents
              :totalItems="filteredItems.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getPackage } from '../../services/packageService';
import PaginationComponents from '../../components/PaginationComponents.vue';
import EmptyState from '../../components/EmptyState.vue';

export default {
  name: 'ListPackageView',
  components: {
    PaginationComponents,
    EmptyState,
  },
  data() {
    return {
      packages: [],
      search: '',
      currentPage: 1,
      itemsPerPage: 10,
      loading: false,
    };
  },
  computed: {
    filteredItems() {
      if (!this.search) return this.packages;
      const q = this.search.toLowerCase();
      return this.packages.filter((item) => {
        const nameMatch = (item.package_name || '').toLowerCase().includes(q);
        const descMatch = (item.description || '').toLowerCase().includes(q);
        return nameMatch || descMatch;
      });
    },
    paginatedPackages() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredItems.slice(start, end);
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const res = await getPackage();
        this.packages = res.data || res || [];
      } catch (err) {
        console.error('Error fetching packages:', err);
      } finally {
        this.loading = false;
      }
    },
    formatRupiah(value) {
      if (!value) return 'Rp 0';
      return `Rp ${parseInt(value).toLocaleString('id-ID')}`;
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
  },
};
</script>

<style scoped>
.btn-action-icon {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  line-height: 1 !important;
  transition: all 0.15s ease-in-out;
}
.btn-action-icon:hover {
  transform: translateY(-1px);
}
</style>